import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Eye, EyeOff, Loader2, Copy, Check } from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';

export default function AuthenticationHub() {
  const navigate = useNavigate();
  const { signIn, signUp, needsOnboarding } = useAuth();
  
  const emailInputRef = useRef(null);
  const passwordInputRef = useRef(null);
  
  const [isLogin, setIsLogin] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [copiedError, setCopiedError] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: '',
    crp: ''
  });

  // Auto-focus email field on component mount and when switching auth modes
  useEffect(() => {
    const timer = setTimeout(() => {
      emailInputRef?.current?.focus();
    }, 100);

    return () => clearTimeout(timer);
  }, [isLogin]);

  // Focus password field when incorrect credentials error occurs
  useEffect(() => {
    if (error && error?.includes('Email ou senha incorretos')) {
      const timer = setTimeout(() => {
        passwordInputRef?.current?.focus();
        passwordInputRef?.current?.select();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const validateForm = () => {
    if (!formData?.email || !formData?.password) {
      setError('Email e senha são obrigatórios');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex?.test(formData?.email)) {
      setError('Por favor, insira um email válido');
      return false;
    }

    if (formData?.password?.length < 6) {
      setError('A senha deve ter no mínimo 6 caracteres');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      if (isLogin) {
        const { error: signInError } = await signIn(formData?.email, formData?.password);
        
        if (signInError) {
          if (signInError?.message?.includes('Invalid login credentials')) {
            setError('Email ou senha incorretos. Verifique suas credenciais e tente novamente.');
          } else if (signInError?.message?.includes('Email not confirmed')) {
            setError('Por favor, confirme seu email primeiro.');
          } else {
            setError(signInError?.message || 'Erro ao fazer login. Tente novamente.');
          }
          return;
        }

        setSuccessMessage('Login realizado com sucesso!');
        
        // Wait for profile to load and check onboarding status
        setTimeout(() => {
          // Use a callback to check needsOnboarding after state update
          const checkOnboarding = () => {
            if (needsOnboarding) {
              navigate('/onboarding-flow');
            } else {
              navigate('/schedule-dashboard');
            }
          };
          checkOnboarding();
        }, 1500);
      } else {
        const { error: signUpError } = await signUp(
          formData?.email, 
          formData?.password,
          {
            full_name: formData?.fullName,
            crp: formData?.crp
          }
        );
        
        if (signUpError) {
          if (signUpError?.message?.includes('already registered')) {
            setError('Este email já está cadastrado. Faça login ou use outro email.');
          } else {
            setError(signUpError?.message || 'Erro ao criar conta. Tente novamente.');
          }
          return;
        }

        setSuccessMessage('Conta criada com sucesso! Foi enviado um link de confirmação para seu email.');
        setTimeout(() => {
          navigate('/authentication-hub', { replace: true });
        }, 5000)

      }
    } catch (err) {
      console.error('Auth error:', err);
      setError(err?.message || 'Ocorreu um erro inesperado. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyError = () => {
    if (error) {
      navigator.clipboard?.writeText(error);
      setCopiedError(true);
      setTimeout(() => setCopiedError(false), 2000);
    }
  };

  const toggleAuthMode = () => {
    setIsLogin(!isLogin);
    setError('');
    setSuccessMessage('');
    setFormData({
      email: '',
      password: '',
      fullName: '',
      crp: ''
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-blue-50 to-pink-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {isLogin ? 'Bem-vindo de volta!' : 'Criar Conta'}
            </h1>
            <p className="text-gray-600">
              {isLogin ? 'Faça login para acessar sua agenda' : (
                <>
                  Cadastre-se para gerenciar seus atendimentos
                  <br />
                  <span className="text-red-400">(14 dias grátis)</span>
                </>
              )
              }
            </p>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-600" />
                <p className="text-sm text-green-800">{successMessage}</p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm text-red-800 flex-1">{error}</p>
                <button
                  onClick={handleCopyError}
                  className="text-red-600 hover:text-red-800 transition-colors flex-shrink-0"
                  title="Copiar mensagem de erro"
                >
                  {copiedError ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Test Credentials Info - Only for Login */}
          {isLogin && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg hidden">
              <p className="text-sm font-semibold text-blue-900 mb-2">
                🔐 Credenciais de Teste:
              </p>
              <div className="text-sm text-blue-800 space-y-1">
                <p><span className="font-medium">Email:</span> dra.silva@example.com</p>
                <p><span className="font-medium">Senha:</span> Test@123</p> 
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email *
              </label>
              <Input
                ref={emailInputRef}
                id="email"
                name="email"
                type="email"
                value={formData?.email}
                onChange={handleInputChange}
                placeholder="seu@email.com"
                disabled={isLoading}
                required
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Senha *
              </label>
              <div className="relative">
                <Input
                  ref={passwordInputRef}
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData?.password}
                  onChange={handleInputChange}
                  placeholder="••••••••"
                  disabled={isLoading}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  disabled={isLoading}
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full h-12 text-base font-semibold bg-[#2563EB] hover:bg-[#1e40af] text-white"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {isLogin ? 'Entrando...' : 'Criando conta...'}
                </span>
              ) : (
                <span>{isLogin ? 'Entrar' : 'Criar Conta'}</span>
              )}
            </Button>
          </form>

          {/* Toggle Auth Mode */}
          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={toggleAuthMode}
              disabled={isLoading}
              className="text-sm text-[#2563EB] hover:text-[#1e40af] font-medium disabled:opacity-50 transition-colors"
            >
              {isLogin 
                ? 'Não tem uma conta? Cadastre-se' :'Já tem uma conta? Faça login'}
            </button>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-gray-600 mt-8">
          {import.meta.env.VITE_APP_TITLE} - Gestão de Agenda para Psicólogos
        </p>
      </div>
    </div>
  );
}